import React from 'react';
import {StyleSheet, View} from 'react-native';
import Animated, {FadeIn} from 'react-native-reanimated';
import {Card} from '@rneui/themed';
import {observer} from 'mobx-react-lite';

import NoTillPaymentServiceAvailableModal from '../../../../components/modals/NoPaymentConfiguredModal';
import CardTokeniserTillModal from '../../../../components/payments/till/cardTokeniser/CardTokeniserTillModal';
import PaymentMethodSelector from '../../../../components/payments/till/PaymentMethodSelector';
import {AppRoute} from '../../../../models/AppRoute';
import GiftCardsSection from '../../components/GiftCardsSection';
import {useCardManagement} from '../../hooks';
import type {StepContentProps} from '../types';

/**
 * Left side content for Payment step
 *
 * Contains:
 * - PaymentMethodSelector (card, Apple Pay, Google Pay)
 * - Gift cards manager
 *
 * Note: PickupDeliveryDetails is now in the right sidebar
 * (UnifiedCheckoutScreen) for web desktop layout.
 *
 * Payment method state is lifted to UnifiedCheckoutScreen for CheckoutActionButton.
 */
const PaymentContent = observer(
  ({
    selectedPaymentMethod,
    onPaymentMethodChange,
    applePayConfig,
    onCanPayChange,
  }: StepContentProps) => {
    // Runtime validation - these props are required for PaymentContent
    if (!selectedPaymentMethod || !onPaymentMethodChange) {
      throw new Error(
        'PaymentContent requires selectedPaymentMethod and onPaymentMethodChange props',
      );
    }

    // Card management from consolidated hook (fetches card via TanStack Query)
    const {
      isCardActionLoading,
      handleAddOrChangeCard,
      handleDeleteCard,
      isCardTokeniserModalVisible,
      setCardTokeniserModalVisible,
      isNoPaymentServiceModalVisible,
      setNoPaymentServiceModalVisible,
    } = useCardManagement();

    return (
      <View style={styles.container}>
        <Animated.View entering={FadeIn} style={styles.fullWidth}>
          <Card noMargin variant="outlined" containerStyle={styles.noPadding}>
            <PaymentMethodSelector
              selectedMethod={selectedPaymentMethod}
              onMethodChange={onPaymentMethodChange}
              onAddCard={handleAddOrChangeCard}
              onChangeCard={handleAddOrChangeCard}
              onDeleteCard={handleDeleteCard}
              isCardActionLoading={isCardActionLoading}
              applePayConfig={applePayConfig}
              onCanPayChange={onCanPayChange}
            />
          </Card>
        </Animated.View>

        <GiftCardsSection testID="payment-content-gift-cards" />

        <CardTokeniserTillModal
          isVisible={isCardTokeniserModalVisible}
          onCancel={() => setCardTokeniserModalVisible(false)}
          navigationTarget={AppRoute.CART_ORDER_CHECKOUT_PAYMENT}
        />
        <NoTillPaymentServiceAvailableModal
          onOK={() => setNoPaymentServiceModalVisible(false)}
          isVisible={isNoPaymentServiceModalVisible}
        />
      </View>
    );
  },
);

export default PaymentContent;

const styles = StyleSheet.create({
  container: {
    gap: 16,
    width: '100%',
  },
  fullWidth: {
    width: '100%',
  },
  noPadding: {
    padding: 0,
  },
});
